if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="Slider-Gallery"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      
      // Ensure featured image is visible on both desktop and mobile
      if (this.elements.viewer) {
        setTimeout(() => {
          this.ensureFirstImageVisible();
        }, 100);
      }
      
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
    }

    connectedCallback() {
      // Ensure featured image is visible when component connects (both desktop and mobile)
      if (this.elements.viewer) {
        setTimeout(() => {
          this.ensureFirstImageVisible();
        }, 150);
      }
    }
    
    ensureFirstImageVisible() {
      if (!this.elements.viewer) return;
      
      // Find the active media item (should be the featured image from Liquid)
      let activeMedia = this.elements.viewer.querySelector('.product__media-item.is-active[data-media-id]');
      
      // If no active item found, try to find the first image (not video/model)
      if (!activeMedia) {
        const allMedia = this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]');
        for (let i = 0; i < allMedia.length; i++) {
          // Check if it's an image (not a video or model)
          const mediaItem = allMedia[i];
          if (!mediaItem.classList.contains('product__media-item--full')) {
            activeMedia = mediaItem;
            break;
          }
        }
        // If still no image found, use first child
        if (!activeMedia) {
          activeMedia = this.elements.viewer.querySelector('.product__media-item[data-media-id]:first-child');
        }
      }
      
      if (activeMedia) {
        // Ensure it has is-active class
        activeMedia.classList.add('is-active');
        
        // Desktop: hide/show logic
        if (this.mql && this.mql.matches) {
          // Remove any inline styles that might hide it
          activeMedia.style.removeProperty('display');
          activeMedia.style.removeProperty('opacity');
          activeMedia.style.removeProperty('visibility');
          
          // Hide other items on desktop
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
            if (element !== activeMedia) {
              element.classList.remove('is-active');
              element.style.setProperty('display', 'none', 'important');
              element.style.setProperty('opacity', '0', 'important');
              element.style.setProperty('visibility', 'hidden', 'important');
            }
          });
        } else {
          // Mobile: move the active slide to the beginning of the list so it shows first
          // This ensures the featured image appears first on mobile load
          if (activeMedia.parentElement && activeMedia !== activeMedia.parentElement.firstElementChild) {
            activeMedia.parentElement.insertBefore(activeMedia, activeMedia.parentElement.firstElementChild);
          }
          
          // Wait for images to load before setting scroll position
          this.waitForImagesAndScrollToStart(activeMedia);
        }
      }
    }
    
    waitForImagesAndScrollToStart(activeMedia) {
      if (!this.elements.viewer || !activeMedia) return;
      
      // Find all images in the active media item
      const images = activeMedia.querySelectorAll('img');
      let loadedCount = 0;
      const totalImages = images.length;
      
      // If no images, scroll immediately
      if (totalImages === 0) {
        this.scrollToStart();
        return;
      }
      
      // Function to check if we should scroll
      const checkAndScroll = () => {
        loadedCount++;
        if (loadedCount >= totalImages) {
          // All images loaded, now scroll to start
          // Use multiple attempts to ensure scroll position is set correctly
          this.scrollToStart();
          setTimeout(() => this.scrollToStart(), 50);
          setTimeout(() => this.scrollToStart(), 200);
        }
      };
      
      // Check each image
      images.forEach((img) => {
        if (img.complete && img.naturalHeight !== 0) {
          // Image already loaded
          checkAndScroll();
        } else {
          // Wait for image to load
          img.addEventListener('load', checkAndScroll, { once: true });
          img.addEventListener('error', checkAndScroll, { once: true });
          
          // Force load if it's lazy loaded
          if (img.dataset.src) {
            img.src = img.dataset.src;
            delete img.dataset.src;
          }
          if (img.dataset.srcset) {
            img.srcset = img.dataset.srcset;
            delete img.dataset.srcset;
          }
        }
      });
      
      // Fallback: scroll after a maximum wait time
      setTimeout(() => {
        this.scrollToStart();
      }, 1000);
    }
    
    scrollToStart() {
      if (!this.elements.viewer) return;
      
      // Force scroll to start position
      this.elements.viewer.scrollLeft = 0;
      
      // Also try scrollTo if available
      if (this.elements.viewer.scrollTo) {
        this.elements.viewer.scrollTo({ left: 0, top: 0, behavior: 'instant' });
      }
      
      // Force a reflow to ensure the scroll position is applied
      void this.elements.viewer.offsetHeight;
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia) return;
      
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      
      // Force display update on desktop - explicitly show/hide media items
      if (this.mql && this.mql.matches) {
        // Hide all media items first
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.style.setProperty('display', 'none', 'important');
          element.style.setProperty('opacity', '0', 'important');
          element.style.setProperty('visibility', 'hidden', 'important');
        });
        // Show the active media item
        activeMedia.style.setProperty('display', 'block', 'important');
        activeMedia.style.setProperty('opacity', '1', 'important');
        activeMedia.style.setProperty('visibility', 'visible', 'important');
        // Force a reflow to ensure CSS updates
        void activeMedia.offsetHeight;
      }
      
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (prepend && (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked')) {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }

    nextMedia() {
      const active = this.elements.viewer.querySelector('.is-active[data-media-id]');
      if (!active) return;
      const all = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')).filter(el => el.children.length > 0);
      const idx = all.indexOf(active);
      if (idx === -1) return;
      const nextIdx = (idx + 1) % all.length;
      const next = all[nextIdx];
      this.setActiveMedia(next.getAttribute('data-media-id'), false);
    }

    prevMedia() {
      const active = this.elements.viewer.querySelector('.is-active[data-media-id]');
      if (!active) return;
      const all = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')).filter(el => el.children.length > 0);
      const idx = all.indexOf(active);
      if (idx === -1) return;
      const prevIdx = (idx - 1 + all.length) % all.length;
      const prev = all[prevIdx];
      this.setActiveMedia(prev.getAttribute('data-media-id'), false);
    }
  });
}
